import type { EventSummary, EventsPage } from '@finder/contracts';

/**
 * View projections. The list and map derive from the same ordered EventsPage.
 * These projections never re-score and never reorder: server order is
 * authoritative here. The map omits events with no real point (unknown
 * locations). A visitor sort (soonest / nearest) is applied later, only to
 * the rendered results list — see list-view.ts — and does not change this
 * projection.
 */

export interface ListItem {
  canonicalId: string;
  title: string;
  timingBand: EventSummary['timingBand'];
  minutesRemaining: number | null;
  distanceMiles: number | null;
  foodState: EventSummary['foodState'];
  primaryHref: string;
  sourceNames: string[];
}

export interface MapMarker {
  canonicalId: string;
  lat: number;
  lng: number;
  title: string;
}

/** Ordered list projection — preserves server order exactly. */
export function projectList(page: EventsPage): ListItem[] {
  return page.events.map((e) => ({
    canonicalId: e.canonicalId,
    title: e.title,
    timingBand: e.timingBand,
    minutesRemaining: e.minutesRemaining,
    distanceMiles: e.distanceMiles,
    foodState: e.foodState,
    primaryHref: e.primarySourceLink.href,
    sourceNames: e.sourceNames,
  }));
}

/**
 * Map projection — one marker per event that has a real point, same order as
 * the list. Unknown locations are omitted so they are not dropped on downtown.
 */
export function projectMap(page: EventsPage): MapMarker[] {
  const markers: MapMarker[] = [];
  for (const e of page.events) {
    if (!e.coordinates || e.locationPrecision === 'unknown') continue;
    markers.push({
      canonicalId: e.canonicalId,
      lat: e.coordinates.lat,
      lng: e.coordinates.lng,
      title: e.title,
    });
  }
  return markers;
}
